import { useMemo, useState } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import {
  Clock, ListChecks, CalendarDays, LogOut, LayoutDashboard, ClipboardList,
  LayoutGrid, MapPin, User, Bell, FileText, Briefcase, Receipt, MoreHorizontal, PartyPopper,
  CalendarRange, Route, CalendarCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEmpresa } from "@/hooks/useEmpresa";
import { useNotificaciones } from "@/hooks/useNotificaciones";
import EmpresaBrand from "@/components/EmpresaBrand";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";
import {
  Drawer, DrawerContent, DrawerHeader, DrawerTitle,
} from "@/components/ui/drawer";

const primaryItems = [
  { to: "/fichar", label: "Fichar", icon: Clock, active: "text-indigo-600" },
  { to: "/mis-notificaciones", label: "Avisos", icon: Bell, active: "text-amber-600", badge: true },
  { to: "/calendario-picados", label: "Calendario", icon: CalendarCheck, active: "text-emerald-600" },
  { to: "/mis-fichajes", label: "Picados", icon: ListChecks, active: "text-sky-600" },
];

const secondaryBase = [
  { to: "/mi-cuadro", label: "Cuadro", desc: "Resumen anual de ausencias", icon: LayoutGrid, active: "text-emerald-600" },
  { to: "/mi-calendario", label: "Mi horario", desc: "Calendario y turnos", icon: CalendarRange, active: "text-indigo-600" },
  { to: "/mis-festivos", label: "Festivos", desc: "Días no laborables", icon: PartyPopper, active: "text-orange-600" },
  { to: "/mis-documentos", label: "Documentos", desc: "Nóminas y contratos", icon: FileText, active: "text-slate-600" },
  { to: "/mis-informes", label: "Informes", desc: "Consultas y listados", icon: ClipboardList, active: "text-violet-600" },
  { to: "/mis-solicitudes", label: "Solicitudes", desc: "Vacaciones y permisos", icon: CalendarDays, active: "text-rose-600" },
];

const zonaItem = {
  to: "/mis-zonas", label: "Zonas", desc: "Tu planificación diaria", icon: MapPin, active: "text-fuchsia-600",
};

const proyectoItem = {
  to: "/mis-proyectos", label: "Proyectos", desc: "Asignaciones activas", icon: Briefcase, active: "text-violet-600",
};

const gastoItem = {
  to: "/mis-gastos", label: "Gastos", desc: "Tickets y facturas", icon: Receipt, active: "text-emerald-600",
};

const rutaItem = {
  to: "/mis-rutas", label: "Mi ruta", desc: "Visitas comerciales de hoy", icon: Route, active: "text-rose-600",
};

function buildSecondaryItems(empresa) {
  const items = [...secondaryBase];
  if (empresa?.gestionar_zonas) {
    items.unshift(zonaItem);
  }
  let idx = empresa?.gestionar_zonas ? 1 : 0;
  if (empresa?.gestionar_proyectos) {
    items.splice(idx, 0, proyectoItem);
    idx += 1;
  }
  if (empresa?.gestionar_gastos) {
    items.splice(idx, 0, gastoItem);
    idx += 1;
  }
  if (empresa?.gestionar_rutas_comerciales) {
    items.splice(idx, 0, rutaItem);
  }
  return items;
}

function NavIcon({ to, label, icon: Icon, active, badge, totalPendientes, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`worker-nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `relative flex min-w-0 flex-1 flex-col items-center gap-1 px-1 py-2.5 text-[11px] leading-tight transition-colors ${
          isActive ? `${active} font-semibold` : "text-zinc-400 hover:text-zinc-600"
        }`
      }
    >
      <Icon className="h-5 w-5 shrink-0" strokeWidth={1.5} />
      {badge && totalPendientes > 0 && (
        <span className="absolute right-1 top-1.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-rose-500 px-0.5 text-[9px] font-bold text-white">
          {totalPendientes > 9 ? "9+" : totalPendientes}
        </span>
      )}
      <span className="max-w-full truncate">{label}</span>
    </NavLink>
  );
}

export default function WorkerLayout() {
  const { user, logout } = useAuth();
  const { empresa } = useEmpresa();
  const { total_pendientes } = useNotificaciones(30000, "trabajador");
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const secondaryItems = useMemo(() => buildSecondaryItems(empresa), [empresa]);
  const menuActive = secondaryItems.some(
    (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
  );

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-indigo-50/50 via-zinc-50 to-violet-50/30 dark:from-zinc-950 dark:via-zinc-950 dark:to-zinc-900">
      <header className="sticky top-0 z-20 border-b border-indigo-100/80 bg-white/90 backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-950/90">
        <div className="mx-auto max-w-lg px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1">
              <EmpresaBrand empresa={empresa} compact />
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <ThemeToggle />
              <Link
                to="/mi-perfil"
                data-testid="worker-perfil-link"
                className="rounded-lg p-2 text-zinc-400 hover:bg-indigo-50 hover:text-indigo-600"
                title="Mi información"
              >
                <User className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <NotificationBell to="/mis-notificaciones" scope="trabajador" />
              {user &&
                (user.rol !== "worker" ||
                  user.es_aprobador_gerencia ||
                  user.es_miembro_gerencia) && (
                <Link
                  to={
                    user.rol === "worker" &&
                    (user.es_aprobador_gerencia || user.es_miembro_gerencia)
                      ? "/admin/vacaciones"
                      : "/admin"
                  }
                  data-testid="worker-go-admin-link"
                  className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50"
                  title="Ir a gestión"
                >
                  <LayoutDashboard className="h-4 w-4" strokeWidth={1.5} />
                  <span className="hidden sm:inline">Panel</span>
                </Link>
              )}
              <button
                onClick={logout}
                data-testid="worker-logout-btn"
                className="rounded-lg p-2 text-zinc-400 hover:bg-rose-50 hover:text-rose-600"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-lg flex-1 px-4 pb-24 pt-5">
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-20 border-t border-indigo-100/80 bg-white/90 backdrop-blur-xl dark:border-zinc-800 dark:bg-zinc-950/90">
        <div className="mx-auto flex max-w-lg items-stretch px-1">
          {primaryItems.map((item) => (
            <NavIcon key={item.to} {...item} totalPendientes={total_pendientes} />
          ))}
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            data-testid="worker-nav-mas"
            className={`relative flex min-w-0 flex-1 flex-col items-center gap-1 px-1 py-2.5 text-[11px] leading-tight transition-colors ${
              menuActive ? "font-semibold text-indigo-600" : "text-zinc-400 hover:text-zinc-600"
            }`}
          >
            <MoreHorizontal className="h-5 w-5 shrink-0" strokeWidth={1.5} />
            <span className="max-w-full truncate">Más</span>
          </button>
        </div>
      </nav>

      <Drawer open={menuOpen} onOpenChange={setMenuOpen}>
        <DrawerContent className="mx-auto max-w-lg pb-8">
          <DrawerHeader className="border-b border-zinc-100 pb-3 text-left">
            <DrawerTitle className="font-display text-lg">Más opciones</DrawerTitle>
          </DrawerHeader>
          <div className="grid grid-cols-2 gap-2 p-4">
            {secondaryItems.map(({ to, label, desc, icon: Icon, active }) => {
              const isActive = location.pathname === to || location.pathname.startsWith(`${to}/`);
              return (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMenuOpen(false)}
                  data-testid={`worker-nav-${label.toLowerCase()}`}
                  className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${
                    isActive
                      ? "border-indigo-200 bg-indigo-50"
                      : "border-zinc-100 bg-zinc-50/80 hover:border-zinc-200 hover:bg-white"
                  }`}
                >
                  <div className={`mt-0.5 shrink-0 ${isActive ? active : "text-zinc-500"}`}>
                    <Icon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium ${isActive ? active : "text-zinc-800"}`}>{label}</p>
                    <p className="text-[11px] leading-snug text-zinc-500">{desc}</p>
                  </div>
                </NavLink>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
